import { createFileRoute, Link } from "@tanstack/react-router"
import { PublicNavbar } from "@/components/public-navbar"

export const Route = createFileRoute("/terms-of-service")({
  head: () => ({
    meta: [
      { title: "Terms of service | intoshort" },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
  component: LegalPage,
})

function LegalPage() {
  return (
    <div className="landing">
      <a className="skip-link" href="#legal-content">
        Skip to content
      </a>
      <PublicNavbar />
      <main id="legal-content" className="page-width py-16 sm:py-24">
        <article className="mx-auto flex max-w-prose flex-col gap-10 leading-7">
          <header className="flex flex-col gap-4">
            <h1 className="text-3xl font-semibold sm:text-4xl">
              Terms of Service
            </h1>
          </header>
          <section
            aria-labelledby="welcome-to-intoshort"
            className="flex flex-col gap-4"
          >
            <h2
              id="welcome-to-intoshort"
              className="scroll-mt-24 text-xl font-semibold sm:text-2xl"
            >
              Welcome to intoshort
            </h2>
            <p>
              {
                'intoshort helps you turn long videos into short clips using AI, captions and editing tools. "We" means the operator identified in Contact Information. "You" means the person or organization using the service.'
              }
            </p>
          </section>
          <section
            aria-labelledby="acceptance-of-terms"
            className="flex flex-col gap-4"
          >
            <h2
              id="acceptance-of-terms"
              className="scroll-mt-24 text-xl font-semibold sm:text-2xl"
            >
              Acceptance of Terms
            </h2>
            <p>
              {
                "By accepting these Terms, you agree to follow them. You must have legal capacity to enter this agreement and authority to act for any organization you represent."
              }
            </p>
          </section>
          <section
            aria-labelledby="modification-of-terms"
            className="flex flex-col gap-4"
          >
            <h2
              id="modification-of-terms"
              className="scroll-mt-24 text-xl font-semibold sm:text-2xl"
            >
              Modification of Terms
            </h2>
            <p>
              {
                "We may update these Terms for legal, security or service changes. We will notify you of material changes before they apply and obtain consent where required. Changes do not apply retroactively."
              }
            </p>
          </section>
          <section
            aria-labelledby="use-of-service"
            className="flex flex-col gap-4"
          >
            <h2
              id="use-of-service"
              className="scroll-mt-24 text-xl font-semibold sm:text-2xl"
            >
              Use of Service
            </h2>
            <p>
              {
                "Keep your account secure and use only content you are authorized to process. Available features and usage limits depend on your plan. Paid subscriptions renew at the price and frequency disclosed before purchase until cancelled. Annual billing does not make all monthly allowances available immediately."
              }
            </p>
          </section>
          <section
            aria-labelledby="user-content"
            className="flex flex-col gap-4"
          >
            <h2
              id="user-content"
              className="scroll-mt-24 text-xl font-semibold sm:text-2xl"
            >
              User Content
            </h2>
            <p>
              {
                "You retain your rights to uploaded content and edits. You grant us permission to store, process and transmit them through our service providers solely to operate the service for you. This does not authorize promotional use. Review AI selections, captions and framing before publishing, and keep your own copies. Personal data handling is described in our "
              }
              <Link className="text-link text-primary" to="/privacy-policy">
                Privacy Policy
              </Link>
              {"."}
            </p>
            <p>
              {
                "Before publishing to YouTube, you must have all rights needed for the selected render and certify that the publication complies with the "
              }
              <a
                className="text-link text-primary"
                href="https://www.youtube.com/howyoutubeworks/policies/community-guidelines/"
              >
                YouTube Community Guidelines
              </a>
              {
                ". You remain responsible for the title, description, visibility, audience and other publication settings you choose."
              }
            </p>
          </section>
          <section
            aria-labelledby="prohibited-conduct"
            className="flex flex-col gap-4"
          >
            <h2
              id="prohibited-conduct"
              className="scroll-mt-24 text-xl font-semibold sm:text-2xl"
            >
              Prohibited Conduct
            </h2>
            <p>
              {
                "Do not upload unlawful or infringing content, violate privacy, impersonate others, distribute malware, bypass access restrictions or quotas, or disrupt the service. A publicly accessible video does not automatically grant permission to download or reuse it."
              }
            </p>
          </section>
          <section
            aria-labelledby="termination"
            className="flex flex-col gap-4"
          >
            <h2
              id="termination"
              className="scroll-mt-24 text-xl font-semibold sm:text-2xl"
            >
              Termination
            </h2>
            <p>
              {
                "You may stop using the service and contact us to request account closure or subscription cancellation. Stopping use alone does not cancel a subscription. We may suspend or terminate access for material breaches, security threats or legal requirements, with notice and an opportunity to resolve the issue where appropriate. Mandatory refund and data rights remain unaffected."
              }
            </p>
          </section>
          <section
            aria-labelledby="disclaimer-of-warranties"
            className="flex flex-col gap-4"
          >
            <h2
              id="disclaimer-of-warranties"
              className="scroll-mt-24 text-xl font-semibold sm:text-2xl"
            >
              Disclaimer of Warranties
            </h2>
            <p>
              {
                'Subject to mandatory legal guarantees, the service is provided "as is" and "as available". AI results may contain errors or miss useful moments. We do not guarantee uninterrupted access, a specific number of clips, audience growth or viral results.'
              }
            </p>
          </section>
          <section
            aria-labelledby="limitation-of-liability"
            className="flex flex-col gap-4"
          >
            <h2
              id="limitation-of-liability"
              className="scroll-mt-24 text-xl font-semibold sm:text-2xl"
            >
              Limitation of Liability
            </h2>
            <p>
              {
                "To the extent permitted by law, we are not liable for indirect losses or lost profits arising from use of the service. Nothing excludes liability for fraud, intentional misconduct or any liability that cannot legally be excluded, or limits mandatory consumer remedies."
              }
            </p>
          </section>
          <section
            aria-labelledby="governing-law"
            className="flex flex-col gap-4"
          >
            <h2
              id="governing-law"
              className="scroll-mt-24 text-xl font-semibold sm:text-2xl"
            >
              Governing Law
            </h2>
            <p>
              {
                "Applicable law and competent courts are determined by the relevant conflict-of-law and jurisdiction rules. These Terms do not remove mandatory protections or rights to bring proceedings available to consumers where they live."
              }
            </p>
          </section>
          <section
            aria-labelledby="refund-policy"
            className="flex flex-col gap-4"
          >
            <h2
              id="refund-policy"
              className="scroll-mt-24 text-xl font-semibold sm:text-2xl"
            >
              Refund Policy
            </h2>
            <p>
              {
                "Mandatory withdrawal, refund and digital-service conformity rights apply. Where applicable, consumers generally have 14 days to withdraw from an online service contract. Starting processing does not automatically waive that right. Any lawful exception requires its applicable conditions to be met. Contact us to request a refund. Additional commercial refund conditions remain to be finalized before paid launch."
              }
            </p>
          </section>
          <section
            aria-labelledby="third-party-services"
            className="flex flex-col gap-4"
          >
            <h2
              id="third-party-services"
              className="scroll-mt-24 text-xl font-semibold sm:text-2xl"
            >
              Third-Party Services
            </h2>
            <p>
              {
                "intoshort uses external providers for functions such as authentication, payments and processing. If you ask intoshort to publish to YouTube, we send the selected render and publication metadata to Google and YouTube for that purpose. Their applicable terms govern your direct use of their services. External platform restrictions may prevent imports or publication. Our use of providers does not remove our own legal obligations to you."
              }
            </p>
          </section>
          <section
            aria-labelledby="managing-third-party-account-access"
            className="flex flex-col gap-4"
          >
            <h2
              id="managing-third-party-account-access"
              className="scroll-mt-24 text-xl font-semibold sm:text-2xl"
            >
              Managing Third-Party Account Access
            </h2>
            <p>
              {
                "You can revoke connected account permissions in the provider's security settings. Revocation does not itself delete intoshort data, end existing sessions or cancel subscriptions. Disconnecting YouTube or deleting local publication data does not delete a video already sent to YouTube. You must delete it through YouTube. Contact us before removing your only sign-in method."
              }
            </p>
          </section>
          <section
            aria-labelledby="contact-information"
            className="flex flex-col gap-4"
          >
            <h2
              id="contact-information"
              className="scroll-mt-24 text-xl font-semibold sm:text-2xl"
            >
              Contact Information
            </h2>
            <ul className="flex list-disc flex-col gap-2 pl-5 break-words">
              <li>{"Operator: Tom LAU"}</li>
              <li>{"Legal status: Sole proprietor"}</li>
              <li>
                {
                  "Business address: 25 Le Bois Dormant, 77123 Noisy-sur-École, France"
                }
              </li>
              <li>{"Email: tom.lau.974@gmail.com"}</li>
              <li>{"VAT: Not applicable"}</li>
            </ul>
          </section>
          <section
            aria-labelledby="entire-agreement"
            className="flex flex-col gap-4"
          >
            <h2
              id="entire-agreement"
              className="scroll-mt-24 text-xl font-semibold sm:text-2xl"
            >
              Entire Agreement
            </h2>
            <p>
              {
                "These Terms and the plan conditions accepted at purchase form the agreement for your use of intoshort. If a provision is unenforceable, the remaining provisions continue to apply. Nothing overrides mandatory legal rights."
              }
            </p>
          </section>
        </article>
      </main>
    </div>
  )
}
